import {
    Component,
    EventEmitter,
    Input,
    Output,
    HostListener,
} from '@angular/core';
import {
    AuctionCatalogsService,
    LotItemUserNote,
    Response,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { ToggleService } from '../../../services/toggle.service';

/** Max length of a lot user note, enforced client-side. Notes are plain text
 *  (no rich-text markup), so this is exactly the stored/sent length — the
 *  textarea's maxlength blocks typing past it. */
const NOTE_MAX_LENGTH: number = 200;

@Component({
    selector: 'vb-lot-item-user-notes',
    templateUrl: './lot-item-user-notes.component.html',
    styleUrls: ['./lot-item-user-notes.component.scss'],
})
export class LotItemUserNotesComponent {
    @Output() closeNotes: EventEmitter<void> = new EventEmitter<void>();
    @Output() addNote: EventEmitter<LotItemUserNote> =
        new EventEmitter<LotItemUserNote>();
    @Output() removeNote: EventEmitter<string> = new EventEmitter<string>();
    @Output() updateNote: EventEmitter<LotItemUserNote> =
        new EventEmitter<LotItemUserNote>();
    @Input() lotItemUserNotes!: LotItemUserNote[];
    @Input() lotId!: string;
    showNotesEditor: boolean = false;
    private successToastId: number = 0;

    isEditMode: boolean = false;
    isMenuOpen = false;
    activeMenuIndex: number | null = null;
    noteEditorLabel: string = 'Add Note';
    noteId: string = '';
    noteFormGroup: FormGroup = this._formBuilder.group({
        note: [
            '',
            [Validators.required, Validators.maxLength(NOTE_MAX_LENGTH)],
        ],
    });
    /** Live character count shown under the textarea — the note is plain
     *  text, so this is exactly what will be stored. */
    public noteCharCount: number = 0;
    /** Exposes the cap to the template for the counter and maxlength. */
    public readonly maxNoteLength: number = NOTE_MAX_LENGTH;

    constructor(
        private auctionService: AuctionCatalogsService,
        private snackbarService: VbSnackbarService,
        private sanitizer: DomSanitizer,
        private translate: TranslateService,
        private _formBuilder: FormBuilder,
        public toggleService: ToggleService,
    ) {
        this.noteFormControl.valueChanges.subscribe((value: string | null) => {
            this.noteCharCount = (value ?? '').length;
        });
    }

    get noteFormControl(): FormControl {
        return this.noteFormGroup.get('note') as FormControl;
    }

    closeComponent(): void {
        this.closeNotes.emit();
    }

    showEditor(): void {
        this.showNotesEditor = !this.showNotesEditor;
        if (!this.isEditMode) {
            this.noteFormGroup.patchValue({
                note: '',
            });
            this.noteId = '';
        }
        this.noteEditorLabel = 'Add Note';
    }

    /**
     * Frontend cap: the note must have non-blank text and be at most
     * NOTE_MAX_LENGTH characters (the textarea maxlength already prevents
     * typing past it — this is the defensive backstop). Surfaces the relevant
     * error toast and returns false when the note is invalid.
     */
    private isNoteValid(): boolean {
        const value: string = this.noteFormControl.value ?? '';
        if (!value.trim()) {
            this.snackbarService.error(
                this.translate.instant('LotDetails.errorEnterNote'),
            );
            return false;
        }
        if (value.length > NOTE_MAX_LENGTH) {
            this.snackbarService.error(
                this.translate.instant('LotDetails.errorNoteLengthExceeded'),
            );
            return false;
        }
        return true;
    }

    async saveNote(): Promise<void> {
        if (!this.isNoteValid()) {
            return;
        }
        await this.auctionService
            .createLotItemUserNote({
                note: this.noteFormGroup.controls['note'].value,
                lotsId: this.lotId,
            })
            .subscribe({
                next: (res: Response) => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant('LotDetails.noteAddSuccess'),
                    );
                    this.showEditor();
                    this.addNote.emit(res.data);
                },
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant('LotDetails.noteAddError'),
                    );
                },
            });
    }

    sanitizeHtml(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    applyEditMode(note: LotItemUserNote): void {
        this.activeMenuIndex = null;
        this.noteId = note.id ?? '';
        this.isEditMode = !this.isEditMode;
        this.noteFormGroup.patchValue({
            note: this.toPlainText(note.note ?? ''),
        });
        this.showEditor();
        this.noteEditorLabel = 'Edit Note';
    }

    /** Legacy notes were stored as CKEditor HTML — flatten them to plain text
     *  for the textarea, turning block/line-break boundaries into newlines so
     *  paragraphs don't run together. New notes are already plain text. */
    private toPlainText(value: string): string {
        if (!value.includes('<')) {
            return value;
        }
        const withBreaks: string = value
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/(p|li|div|h[1-6])>/gi, '\n');
        const text: string =
            new DOMParser().parseFromString(withBreaks, 'text/html').body
                .textContent ?? '';
        return text.trim();
    }

    deleteNote(noteId: string): void {
        this.activeMenuIndex = null;
        this.auctionService.deleteLotItemUserNoteById(noteId).subscribe({
            next: () => {
                this.removeNote.emit(noteId);
                this.showCustomToast(noteId);
            },
            error: () => {
                this.snackbarService.error(
                    this.translate.instant('LotDetails.noteDeleteError'),
                );
            },
        });
        this.showNotesEditor = false;
    }

    editNote(): void {
        if (!this.isNoteValid()) {
            return;
        }
        this.auctionService
            .putLotItemUserNoteById(this.noteId ?? '', {
                note: this.noteFormGroup.controls['note'].value,
            })
            .subscribe({
                next: (res: Response) => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant('LotDetails.noteEditSuccess'),
                    );
                    this.updateNote.emit(res.data);
                    this.isEditMode = false;
                    this.showEditor();
                },
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant('LotDetails.noteEditError'),
                    );
                },
            });
    }

    /** vb-snackbar has no custom-template mechanism (unlike ngx-toastr's
     *  toastComponent), so the previous UndoToastComponent — a full custom
     *  toast with its own icon/link — is replaced with the built-in
     *  action button. */
    showCustomToast(noteId: string): void {
        this.snackbarService.success(
            this.translate.instant('LotDetails.undoToastTitle'),
            {
                action: {
                    label: this.translate.instant('LotDetails.undoAction'),
                    run: () => this.restoreLotItemUserNote(noteId),
                },
            },
        );
    }

    private restoreLotItemUserNote(noteId: string): void {
        this.auctionService.restoreLotItemUserNoteById(noteId).subscribe(() => {
            window.location.reload();
        });
    }

    toggleNotesActionDropdown(index: number, event: any): void {
        event.stopPropagation();
        this.activeMenuIndex = this.activeMenuIndex === index ? null : index;
    }

    onDropdownClick(event: any): void {
        event.stopPropagation(); // Prevent menu from closing
    }

    // Close the dropdown menu if clicked outside
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (
            this.activeMenuIndex !== null &&
            !(event.target as HTMLElement).closest('.dropdown-menu, .menu-icon')
        ) {
            this.activeMenuIndex = null;
        }
    }
}
